import { useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { extractEmailDomain } from '../utils/email';

const CACHE_KEY = 'p4a-domain-blocklist-cache-v1';
const CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 6; // 6 hours

type BlocklistRecord = {
  domains: string[];
  updatedAt?: string | null;
  fetchedAt: number;
};

type DomainBlocklistResponse = {
  data?: {
    domains?: string[];
    updatedAt?: string;
    lastSyncedFromFile?: string;
  };
  meta?: {
    domainCount?: number;
  };
};

function readCache(): BlocklistRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BlocklistRecord;
    if (!parsed?.domains || !Array.isArray(parsed.domains)) return null;
    if (!parsed.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_MAX_AGE_MS) return null;
    return { ...parsed, domains: normalizeDomains(parsed.domains) };
  } catch {
    return null;
  }
}

function writeCache(record: BlocklistRecord) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(record));
  } catch {
    // ignore
  }
}

function normalizeDomains(domains: unknown): string[] {
  if (!Array.isArray(domains)) return [];
  const set = new Set<string>();
  domains.forEach((domain) => {
    const normalized = extractEmailDomain(typeof domain === 'string' ? domain : '');
    if (normalized) set.add(normalized);
  });
  return Array.from(set).sort();
}

async function fetchDomainBlocklist(): Promise<BlocklistRecord> {
  const base = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337/api';
  const res = await apiGet(`${base}/domain-blocklist`, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to fetch domain blocklist: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as DomainBlocklistResponse;
  const normalized = normalizeDomains(json?.data?.domains || []);
  const updatedAt = json?.data?.updatedAt || json?.data?.lastSyncedFromFile || null;
  const record: BlocklistRecord = {
    domains: normalized,
    updatedAt,
    fetchedAt: Date.now(),
  };
  writeCache(record);
  return record;
}

export function useDomainBlocklist() {
  const cached = useMemo(() => readCache(), []);
  const hadInitialDataRef = useRef<boolean>(!!cached);

  const query = useQuery<BlocklistRecord, Error>({
    queryKey: ['domain-blocklist'],
    queryFn: fetchDomainBlocklist,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    initialData: cached ?? undefined,
  });

  const domains = query.data?.domains ?? [];
  const updatedAt = query.data?.updatedAt ?? null;
  const domainSet = useMemo(() => new Set(query.data?.domains ?? []), [query.data]);

  const check = useCallback((value: string) => {
    const domain = extractEmailDomain(value);
    if (!domain) return { blocked: false, domain: null };
    return { blocked: domainSet.has(domain), domain };
  }, [domainSet]);

  const hasLoaded = query.isSuccess || query.isError;
  const isReady = hasLoaded || hadInitialDataRef.current;

  return {
    domains,
    updatedAt,
    isLoading: query.isLoading && !hadInitialDataRef.current,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    check,
    isReady,
  };
}
